import { HttpClient, httpResource } from '@angular/common/http';
import { Injectable, computed, inject } from '@angular/core';

import { Observable } from 'rxjs';
import * as z from 'zod';

import { User } from '../schemas';
import { storedResource } from '../shared';
import { Lambda } from './lambda';
import { Messenger } from './messenger';

const Response = z.object({
  Count: z.number(),
  ScannedCount: z.number(),
  Items: z.array(User),
});

@Injectable({ providedIn: 'root' })
export class UserService {
  private http = inject(HttpClient);

  private url = inject(Lambda).getUsersUrl();

  private readonly CACHE_KEY = `lcm:service-cache:user`;

  private usersResource = httpResource<z.infer<typeof Response>>(() => this.url, {
    defaultValue: JSON.parse(window.localStorage.getItem(this.CACHE_KEY) || '{"Items": []}'),
    parse: Response.parse,
  });

  constructor() {
    storedResource(this.usersResource, this.CACHE_KEY, inject(Messenger));
  }

  users = computed(() => this.usersResource.value().Items);
  pending = this.usersResource.isLoading;

  names = computed(() =>
    this.users()
      .filter((it) => it.State === 'active')
      .map((it) => it.FullName)
      .sort(),
  );

  save(instance: User, forEdit = false): Observable<User> {
    let request: Observable<User>;
    if (forEdit) {
      request = this.http.put<User>(this.url + '/' + instance.Email, instance);
    } else {
      request = this.http.post<User>(this.url, instance);
    }

    return request;
  }

  reload() {
    this.usersResource.reload();
  }

  delete(user: User): Observable<User> {
    const request: Observable<User> = this.http.delete<User>(this.url + '/' + user.Email);
    return request;
  }
}
